import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit2, Trash2, Copy, Mail, FileText, Calendar, DollarSign, User, Phone, MapPin, Clock, CheckCircle, XCircle, Send, Download, Save, X, Eye, Paperclip } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import RenterForm from './RenterForm';
import EmailDetailModal from '@/components/EmailDetailModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RenterDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [renter, setRenter] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditDialog, setShowEditDialog] = useState(false);
  
  // Edit mode states for each section
  const [editingSection, setEditingSection] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);

  useEffect(() => {
    fetchRenterData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchRenterData = async () => {
    try {
      setLoading(true);
      const [renterRes, contractsRes, emailsRes] = await Promise.all([
        axios.get(`${API}/renters/${id}`),
        axios.get(`${API}/contracts/renter/${id}`).catch(() => ({ data: [] })),
        axios.get(`${API}/email-logs`).catch(() => ({ data: [] }))
      ]);
      
      setRenter(renterRes.data);
      setContracts(contractsRes.data || []);
      
      // Filter emails for this renter
      const renterEmail = renterRes.data?.email?.toLowerCase();
      const filteredEmails = (emailsRes.data || []).filter(
        log => log.recipient_email?.toLowerCase() === renterEmail
      );
      setEmailLogs(filteredEmails);
    } catch (error) {
      toast.error('Failed to load renter details');
      navigate('/admin/renters');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this renter?')) return;
    
    try {
      await axios.delete(`${API}/renters/${id}`);
      toast.success('Renter deleted');
      navigate('/admin/renters');
    } catch (error) {
      toast.error('Failed to delete renter');
    }
  };

  const handleCopyRenter = async () => {
    try {
      const newRenter = {
        first_name: renter.first_name,
        last_name: renter.last_name,
        email: renter.email,
        phone: renter.phone,
        address: renter.address,
        city: renter.city,
        province: renter.province,
        postal_code: renter.postal_code,
        country: renter.country,
        referred_by: 'Returning Guest',
        check_in_date: '',
        check_out_date: '',
        num_guests: renter.num_guests,
        status: 'pending'
      };
      
      const response = await axios.post(`${API}/renters`, newRenter);
      toast.success('Renter copied! Edit the new booking dates.');
      navigate(`/admin/renters/${response.data.id}`);
    } catch (error) {
      toast.error('Failed to copy renter');
    }
  };

  const handleConvertToInquiry = async () => {
    if (!window.confirm('Convert this renter back to an inquiry? This will free up their booked dates.')) return;
    
    try {
      await axios.post(`${API}/renters/convert-to-inquiry`, { renter_id: id });
      toast.success('Converted to inquiry');
      navigate('/admin/inquiries');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert');
    }
  };

  const handleSendEmail = () => {
    const params = new URLSearchParams({
      to: renter.email || '',
      firstName: renter.first_name || '',
      lastName: renter.last_name || '',
      checkIn: renter.check_in_date || '',
      checkOut: renter.check_out_date || '',
      guests: renter.num_guests || '',
      rentalAmount: renter.rental_amount || '',
      totalWithHst: renter.total_with_hst || '',
      type: 'renter',
    });
    navigate(`/admin/email?${params.toString()}`);
  };

  const [previewData, setPreviewData] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  const handlePreviewContract = async () => {
    try {
      setPreviewLoading(true);
      // Get contract data including Terms & Conditions
      const response = await axios.post(`${API}/contracts/preview-data`, { renter_id: id });
      setPreviewData(response.data);
      setShowPreviewModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate contract preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownloadPreviewPdf = async () => {
    try {
      const response = await axios.post(`${API}/contracts/preview`, { renter_id: id }, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      toast.success('Contract PDF opened in new tab');
    } catch (error) {
      toast.error('Failed to generate PDF preview');
    }
  };

  const handleSendContract = async () => {
    try {
      await axios.post(`${API}/contracts`, { renter_id: id });
      toast.success(`Contract sent to ${renter.email}`);
      setShowPreviewModal(false);
      fetchRenterData();
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (detail.includes('pending')) {
        toast.error('A pending contract already exists. Use "Resend" to send again.');
      } else if (detail.includes('signed')) {
        toast.error('A signed contract already exists for this renter.');
      } else {
        toast.error(detail || 'Failed to send contract');
      }
    }
  };

  const handleResendContract = async (contractId) => {
    try {
      await axios.post(`${API}/contracts/${contractId}/resend`);
      toast.success('Contract email resent');
      fetchRenterData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend contract');
    }
  };

  const handleDownloadPdf = async (contractId, renterName) => {
    try {
      const response = await axios.get(`${API}/contracts/${contractId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contract_${renterName.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleFormSave = async (data) => {
    try {
      await axios.put(`${API}/renters/${id}`, data);
      toast.success('Renter updated');
      setShowEditDialog(false);
      fetchRenterData();
    } catch (error) {
      toast.error('Failed to update renter');
    }
  };

  // Start editing a section
  const startEditing = (section) => {
    setEditingSection(section);
    // Pre-populate edit data based on section, mapping backend field names to frontend
    if (section === 'info') {
      setEditData({
        first_name: renter.first_name || '',
        last_name: renter.last_name || '',
        email: renter.email || '',
        phone: renter.cell_phone || renter.phone || '',
        address: renter.address || '',
        city: renter.city || '',
        province: renter.province_state || renter.province || '',
        postal_code: renter.postal_code || '',
        country: renter.country || 'Canada',
        num_guests: renter.num_guests || '',
        referred_by: renter.referred_by || '',
        notes: renter.comments || renter.notes || '',
      });
    } else if (section === 'rental') {
      setEditData({
        check_in_date: renter.check_in_date || '',
        check_out_date: renter.check_out_date || '',
        status: renter.status || 'pending',
      });
    } else if (section === 'payment') {
      setEditData({
        rental_amount: renter.rental_amount || 0,
        hst_percent: renter.hst_percent || 13,
        total_with_hst: renter.total_with_hst || 0,
        payment_1_amount: renter.payment_1_amount || 0,
        payment_1_date: renter.payment_1_date || '',
        payment_2_amount: renter.payment_2_amount || 0,
        payment_2_date: renter.payment_2_date || '',
        payment_3_amount: renter.payment_3_amount || 0,
        payment_3_date: renter.payment_3_date || '',
      });
    }
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingSection(null);
    setEditData({});
  };

  // Save section changes
  const saveSection = async () => {
    setSaving(true);
    try {
      // Build the update data, mapping frontend field names to backend field names
      let updatePayload = {};
      
      if (editingSection === 'info') {
        updatePayload = {
          first_name: editData.first_name || renter.first_name,
          last_name: editData.last_name || renter.last_name,
          email: editData.email || renter.email,
          cell_phone: editData.phone || renter.cell_phone || renter.phone,
          address: editData.address || renter.address,
          city: editData.city || renter.city,
          province_state: editData.province || renter.province_state || renter.province,
          postal_code: editData.postal_code || renter.postal_code,
          country: editData.country || renter.country || 'Canada',
          num_guests: parseInt(editData.num_guests) || renter.num_guests || 0,
          referred_by: editData.referred_by || renter.referred_by,
          comments: editData.notes || renter.comments || renter.notes,
          // Preserve existing fields
          check_in_date: renter.check_in_date,
          check_out_date: renter.check_out_date,
          rental_amount: renter.rental_amount || 0,
          hst_percent: renter.hst_percent || 13,
          total_with_hst: renter.total_with_hst || 0,
          payment_1_amount: renter.payment_1_amount || 0,
          payment_1_date: renter.payment_1_date,
          payment_2_amount: renter.payment_2_amount || 0,
          payment_2_date: renter.payment_2_date,
          payment_3_amount: renter.payment_3_amount || 0,
          payment_3_date: renter.payment_3_date,
        };
      } else if (editingSection === 'rental') {
        updatePayload = {
          // Preserve existing fields
          first_name: renter.first_name,
          last_name: renter.last_name,
          email: renter.email,
          cell_phone: renter.cell_phone || renter.phone,
          address: renter.address,
          city: renter.city,
          province_state: renter.province_state || renter.province,
          postal_code: renter.postal_code,
          country: renter.country || 'Canada',
          num_guests: renter.num_guests || 0,
          referred_by: renter.referred_by,
          comments: renter.comments || renter.notes,
          // Updated fields
          check_in_date: editData.check_in_date || renter.check_in_date,
          check_out_date: editData.check_out_date || renter.check_out_date,
          status: editData.status || renter.status,
          rental_amount: renter.rental_amount || 0,
          hst_percent: renter.hst_percent || 13,
          total_with_hst: renter.total_with_hst || 0,
          payment_1_amount: renter.payment_1_amount || 0,
          payment_1_date: renter.payment_1_date,
          payment_2_amount: renter.payment_2_amount || 0,
          payment_2_date: renter.payment_2_date,
          payment_3_amount: renter.payment_3_amount || 0,
          payment_3_date: renter.payment_3_date,
        };
      } else if (editingSection === 'payment') {
        const rental = parseFloat(editData.rental_amount) || 0;
        const hstPercent = parseFloat(editData.hst_percent) || 0;
        const calculatedTotal = rental * (1 + hstPercent / 100);
        
        updatePayload = {
          // Preserve existing fields
          first_name: renter.first_name,
          last_name: renter.last_name,
          email: renter.email,
          cell_phone: renter.cell_phone || renter.phone,
          address: renter.address,
          city: renter.city,
          province_state: renter.province_state || renter.province,
          postal_code: renter.postal_code,
          country: renter.country || 'Canada',
          num_guests: renter.num_guests || 0,
          referred_by: renter.referred_by,
          comments: renter.comments || renter.notes,
          check_in_date: renter.check_in_date,
          check_out_date: renter.check_out_date,
          // Updated fields
          rental_amount: rental,
          hst_percent: hstPercent,
          total_with_hst: parseFloat(editData.total_with_hst) || calculatedTotal,
          payment_1_amount: parseFloat(editData.payment_1_amount) || 0,
          payment_1_date: editData.payment_1_date || renter.payment_1_date,
          payment_2_amount: parseFloat(editData.payment_2_amount) || 0,
          payment_2_date: editData.payment_2_date || renter.payment_2_date,
          payment_3_amount: parseFloat(editData.payment_3_amount) || 0,
          payment_3_date: editData.payment_3_date || renter.payment_3_date,
        };
      }
      
      await axios.put(`${API}/renters/${id}`, updatePayload);
      toast.success('Changes saved');
      setEditingSection(null);
      setEditData({});
      fetchRenterData();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // Update edit data with auto-calculation for payment fields
  const updateEditData = (field, value) => {
    setEditData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Auto-calculate total_with_hst when rental_amount or hst_percent changes
      if (field === 'rental_amount' || field === 'hst_percent') {
        const rental = parseFloat(field === 'rental_amount' ? value : prev.rental_amount) || 0;
        const hstPercent = parseFloat(field === 'hst_percent' ? value : prev.hst_percent) || 0;
        updated.total_with_hst = Math.round(rental * (1 + hstPercent / 100) * 100) / 100;
      }
      
      return updated;
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    // Handle date-only strings (YYYY-MM-DD) to avoid timezone issues
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'America/Toronto'
      });
    }
    // Handle full ISO datetime strings
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'America/Toronto'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1B4D3E]"></div>
      </div>
    );
  }

  if (!renter) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Renter not found</p>
        <Button variant="outline" onClick={() => navigate('/admin/renters')} className="mt-4">
          Back to Renters
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/renters')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-[#1B4D3E]">
              {renter.first_name} {renter.last_name}
            </h1>
            <p className="text-gray-500">{renter.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleCopyRenter}>
            <Copy className="w-4 h-4 mr-2" /> Copy for New Booking
          </Button>
          <Button variant="outline" onClick={() => setShowEditDialog(true)}>
            <Edit2 className="w-4 h-4 mr-2" /> Edit All
          </Button>
          <Button variant="outline" className="text-red-600 hover:text-red-700" onClick={handleDelete}>
            <Trash2 className="w-4 h-4 mr-2" /> Delete
          </Button>
        </div>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-3 flex-wrap">
            {/* Preview Contract button - always available */}
            <Button variant="outline" onClick={handlePreviewContract} className="text-purple-600 hover:text-purple-700">
              <Eye className="w-4 h-4 mr-2" /> Preview Contract
            </Button>
            {/* Contract button - context aware */}
            {contracts.length === 0 ? (
              <Button onClick={handleSendContract} className="bg-blue-600 hover:bg-blue-700">
                <FileText className="w-4 h-4 mr-2" /> Send Contract
              </Button>
            ) : contracts.some(c => c.status === 'signed') ? (
              <Button variant="outline" disabled className="text-green-600">
                <CheckCircle className="w-4 h-4 mr-2" /> Contract Signed
              </Button>
            ) : (
              <Button variant="outline" onClick={() => handleResendContract(contracts.find(c => c.status === 'pending')?.id)} className="text-yellow-600 hover:text-yellow-700">
                <Send className="w-4 h-4 mr-2" /> Resend Contract
              </Button>
            )}
            <Button variant="outline" onClick={handleSendEmail}>
              <Mail className="w-4 h-4 mr-2" /> Send Email
            </Button>
            <Button variant="outline" className="text-amber-600 hover:text-amber-700" onClick={handleConvertToInquiry}>
              <User className="w-4 h-4 mr-2" /> Convert to Inquiry
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Renter Details */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" /> Renter Information
            </CardTitle>
            {editingSection === 'info' ? (
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={cancelEditing} disabled={saving}>
                  <X className="h-4 w-4" />
                </Button>
                <Button size="sm" onClick={saveSection} disabled={saving} className="bg-[#1B4D3E] hover:bg-[#163d32]">
                  <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            ) : (
              <Button size="sm" variant="ghost" onClick={() => startEditing('info')}>
                <Edit2 className="h-4 w-4" />
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {editingSection === 'info' ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>First Name</Label>
                    <Input value={editData.first_name} onChange={(e) => updateEditData('first_name', e.target.value)} />
                  </div>
                  <div>
                    <Label>Last Name</Label>
                    <Input value={editData.last_name} onChange={(e) => updateEditData('last_name', e.target.value)} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Email</Label>
                    <Input type="email" value={editData.email} onChange={(e) => updateEditData('email', e.target.value)} />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input value={editData.phone} onChange={(e) => updateEditData('phone', e.target.value)} />
                  </div>
                </div>
                <div>
                  <Label>Address</Label>
                  <Input value={editData.address} onChange={(e) => updateEditData('address', e.target.value)} />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>City</Label>
                    <Input value={editData.city} onChange={(e) => updateEditData('city', e.target.value)} />
                  </div>
                  <div>
                    <Label>Province</Label>
                    <Input value={editData.province} onChange={(e) => updateEditData('province', e.target.value)} />
                  </div>
                  <div>
                    <Label>Postal Code</Label>
                    <Input value={editData.postal_code} onChange={(e) => updateEditData('postal_code', e.target.value)} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Number of Guests</Label>
                    <Input type="number" value={editData.num_guests} onChange={(e) => updateEditData('num_guests', e.target.value)} />
                  </div>
                  <div>
                    <Label>Referred By</Label>
                    <Input value={editData.referred_by} onChange={(e) => updateEditData('referred_by', e.target.value)} />
                  </div>
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea value={editData.notes} onChange={(e) => updateEditData('notes', e.target.value)} rows={3} />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Phone</p>
                  <p className="font-medium">{renter.cell_phone || renter.phone || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Guests</p>
                  <p className="font-medium">{renter.num_guests || '-'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-gray-500">Address</p>
                  <p className="font-medium">
                    {renter.address && `${renter.address}, `}
                    {renter.city && `${renter.city}, `}
                    {renter.province_state || renter.province} {renter.postal_code}
                  </p>
                </div>
                {renter.referred_by && (
                  <div className="col-span-2">
                    <p className="text-gray-500">Referred By</p>
                    <p className="font-medium">{renter.referred_by}</p>
                  </div>
                )}
                {(renter.comments || renter.notes) && (
                  <div className="col-span-2">
                    <p className="text-gray-500">Notes</p>
                    <p className="font-medium whitespace-pre-wrap">{renter.comments || renter.notes}</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Rental Period */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" /> Rental Period
            </CardTitle>
            {editingSection === 'rental' ? (
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={cancelEditing} disabled={saving}>
                  <X className="h-4 w-4" />
                </Button>
                <Button size="sm" onClick={saveSection} disabled={saving} className="bg-[#1B4D3E] hover:bg-[#163d32]">
                  <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            ) : (
              <Button size="sm" variant="ghost" onClick={() => startEditing('rental')}>
                <Edit2 className="h-4 w-4" />
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {editingSection === 'rental' ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Check-in Date</Label>
                    <Input type="date" value={editData.check_in_date} onChange={(e) => updateEditData('check_in_date', e.target.value)} />
                  </div>
                  <div>
                    <Label>Check-out Date</Label>
                    <Input type="date" value={editData.check_out_date} onChange={(e) => updateEditData('check_out_date', e.target.value)} />
                  </div>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={editData.status} onValueChange={(value) => updateEditData('status', value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Check-in</p>
                  <p className="font-medium text-lg">{formatDate(renter.check_in_date)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Check-out</p>
                  <p className="font-medium text-lg">{formatDate(renter.check_out_date)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Status</p>
                  <Badge className={
                    renter.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                    renter.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }>
                    {renter.status || 'pending'}
                  </Badge>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payment Details */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" /> Payment Details
            </CardTitle>
            {editingSection === 'payment' ? (
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={cancelEditing} disabled={saving}>
                  <X className="h-4 w-4" />
                </Button>
                <Button size="sm" onClick={saveSection} disabled={saving} className="bg-[#1B4D3E] hover:bg-[#163d32]">
                  <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            ) : (
              <Button size="sm" variant="ghost" onClick={() => startEditing('payment')}>
                <Edit2 className="h-4 w-4" />
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {editingSection === 'payment' ? (
              <div className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label>Rental Amount ($)</Label>
                    <Input type="number" value={editData.rental_amount} onChange={(e) => updateEditData('rental_amount', e.target.value)} />
                  </div>
                  <div>
                    <Label>HST (%)</Label>
                    <Input type="number" value={editData.hst_percent} onChange={(e) => updateEditData('hst_percent', e.target.value)} />
                  </div>
                  <div>
                    <Label>Total with HST ($)</Label>
                    <Input 
                      type="number" 
                      value={editData.total_with_hst?.toFixed(2) || '0.00'} 
                      readOnly
                      className="bg-gray-100"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={async () => {
                        try {
                          const response = await axios.get(`${API}/calculate-price`, {
                            params: {
                              check_in_date: renter.check_in_date,
                              check_out_date: renter.check_out_date
                            }
                          });
                          setEditData(prev => ({
                            ...prev,
                            rental_amount: response.data.rental_amount,
                            hst_percent: response.data.hst_percent,
                            total_with_hst: response.data.total_with_hst
                          }));
                          toast.success('Prices calculated');
                        } catch (error) {
                          toast.error('Failed to calculate prices');
                        }
                      }}
                      className="w-full"
                    >
                      <DollarSign className="h-4 w-4 mr-2" /> Calculate Prices
                    </Button>
                  </div>
                </div>
                <div>
                  <p className="font-medium mb-3">Payment Schedule</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Payment 1 Amount ($)</Label>
                      <Input type="number" value={editData.payment_1_amount} onChange={(e) => updateEditData('payment_1_amount', e.target.value)} />
                      <Label>Due Date</Label>
                      <Input type="date" value={editData.payment_1_date} onChange={(e) => updateEditData('payment_1_date', e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Payment 2 Amount ($)</Label>
                      <Input type="number" value={editData.payment_2_amount} onChange={(e) => updateEditData('payment_2_amount', e.target.value)} />
                      <Label>Due Date</Label>
                      <Input type="date" value={editData.payment_2_date} onChange={(e) => updateEditData('payment_2_date', e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Payment 3 Amount ($)</Label>
                      <Input type="number" value={editData.payment_3_amount} onChange={(e) => updateEditData('payment_3_amount', e.target.value)} />
                      <Label>Due Date</Label>
                      <Input type="date" value={editData.payment_3_date} onChange={(e) => updateEditData('payment_3_date', e.target.value)} />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6">
                  <div>
                    <p className="text-gray-500 text-sm">Rental Amount</p>
                    <p className="font-semibold text-xl">{formatCurrency(renter.rental_amount)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">HST ({renter.hst_percent || 13}%)</p>
                    <p className="font-semibold text-xl">{formatCurrency((renter.rental_amount || 0) * (renter.hst_percent || 13) / 100)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Total</p>
                    <p className="font-semibold text-xl text-[#1B4D3E]">{formatCurrency(renter.total_with_hst || renter.total_amount)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Security Deposit</p>
                    <p className="font-semibold text-xl">{formatCurrency(2500)}</p>
                  </div>
                </div>
                
                {(renter.payment_1_amount || renter.payment_2_amount || renter.payment_3_amount) && (
                  <div>
                    <p className="font-medium mb-3">Payment Schedule</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {renter.payment_1_amount > 0 && (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-500">Payment 1 (Deposit)</p>
                          <p className="font-semibold">{formatCurrency(renter.payment_1_amount)}</p>
                          <p className="text-sm text-gray-500">Due: {renter.payment_1_date || '-'}</p>
                        </div>
                      )}
                      {renter.payment_2_amount > 0 && (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-500">Payment 2</p>
                          <p className="font-semibold">{formatCurrency(renter.payment_2_amount)}</p>
                          <p className="text-sm text-gray-500">Due: {renter.payment_2_date || '-'}</p>
                        </div>
                      )}
                      {renter.payment_3_amount > 0 && (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-500">Payment 3 (Final)</p>
                          <p className="font-semibold">{formatCurrency(renter.payment_3_amount)}</p>
                          <p className="text-sm text-gray-500">Due: {renter.payment_3_date || '-'}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Contracts */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" /> Contracts
            </CardTitle>
          </CardHeader>
          <CardContent>
            {contracts.length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No contracts sent yet</p>
                <div className="flex justify-center gap-2 mt-2">
                  <Button variant="outline" size="sm" onClick={handlePreviewContract}>
                    <Eye className="w-4 h-4 mr-1" /> Preview
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleSendContract}>
                    Send Contract
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {contracts.map((contract) => (
                  <div key={contract.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <div className="flex items-center gap-2">
                        <Badge className={
                          contract.status === 'signed' ? 'bg-green-100 text-green-800' :
                          contract.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }>
                          {contract.status === 'signed' && <CheckCircle className="h-3 w-3 mr-1" />}
                          {contract.status === 'pending' && <Clock className="h-3 w-3 mr-1" />}
                          {contract.status === 'expired' && <XCircle className="h-3 w-3 mr-1" />}
                          {contract.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        Sent: {formatDate(contract.sent_at)}
                        {contract.signed_at && ` • Signed: ${formatDate(contract.signed_at)}`}
                      </p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => handleDownloadPdf(contract.id, renter.renter_name || `${renter.first_name}_${renter.last_name}`)}>
                      <Download className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Email History */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" /> Email History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {emailLogs.length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                <Mail className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No emails sent yet</p>
                <Button variant="outline" size="sm" className="mt-2" onClick={handleSendEmail}>
                  Send Email
                </Button>
              </div>
            ) : (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {emailLogs.map((log) => (
                  <div 
                    key={log.id} 
                    className="p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => setSelectedEmail(log)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{log.subject}</p>
                        <p className="text-xs text-gray-500">{formatDate(log.sent_at)}</p>
                        {(log.attachments_count > 0 || log.attachments?.length > 0) && (
                          <p className="text-xs text-[#1B4D3E] flex items-center gap-1 mt-1">
                            <Paperclip className="w-3 h-3" />
                            {log.attachments?.length || log.attachments_count} attachment(s)
                          </p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-[#1B4D3E] hover:text-[#153E32] flex-shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedEmail(log);
                        }}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Email Detail Modal */}
      <EmailDetailModal 
        email={selectedEmail} 
        open={!!selectedEmail} 
        onClose={() => setSelectedEmail(null)} 
      />

      {/* Contract Preview Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" /> Contract Preview
            </DialogTitle>
          </DialogHeader>
          
          {previewData && (
            <div className="space-y-6">
              {/* Renter Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-[#1B4D3E] mb-3">Renter Information</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-500">Name:</span> {previewData.renter.name}</div>
                  <div><span className="text-gray-500">Email:</span> {previewData.renter.email}</div>
                  <div><span className="text-gray-500">Phone:</span> {previewData.renter.phone || 'N/A'}</div>
                  <div><span className="text-gray-500">Address:</span> {previewData.renter.address || 'N/A'}</div>
                  <div><span className="text-gray-500">City:</span> {previewData.renter.city || 'N/A'}</div>
                  <div><span className="text-gray-500">Province:</span> {previewData.renter.province || 'N/A'}</div>
                </div>
              </div>

              {/* Property & Rental Details */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-[#1B4D3E] mb-3">Rental Details</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-500">Property:</span> {previewData.property.name}</div>
                  <div><span className="text-gray-500">Location:</span> {previewData.property.address}</div>
                  <div><span className="text-gray-500">Check-in:</span> {previewData.rental.check_in_date} at {previewData.rental.check_in_time}</div>
                  <div><span className="text-gray-500">Check-out:</span> {previewData.rental.check_out_date} at {previewData.rental.check_out_time}</div>
                  <div><span className="text-gray-500">Nights:</span> {previewData.rental.num_nights}</div>
                </div>
              </div>

              {/* Financial Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-[#1B4D3E] mb-3">Financial Summary</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-500">Rental Amount:</span> ${previewData.financial.rental_amount?.toLocaleString()}</div>
                  <div><span className="text-gray-500">HST ({previewData.financial.hst_percent}%):</span> ${previewData.financial.hst_amount?.toLocaleString()}</div>
                  <div className="font-semibold"><span className="text-gray-500">Total:</span> ${previewData.financial.total_amount?.toLocaleString()}</div>
                  <div><span className="text-gray-500">Security Deposit:</span> ${previewData.financial.security_deposit?.toLocaleString()}</div>
                </div>
              </div>

              {/* Payment Schedule */}
              {(previewData.payments.payment_1.amount > 0 || previewData.payments.payment_2.amount > 0 || previewData.payments.payment_3.amount > 0) && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-[#1B4D3E] mb-3">Payment Schedule</h3>
                  <div className="text-sm space-y-1">
                    {previewData.payments.payment_1.amount > 0 && (
                      <div>Payment 1 (Deposit): ${previewData.payments.payment_1.amount?.toLocaleString()} - {previewData.payments.payment_1.date}</div>
                    )}
                    {previewData.payments.payment_2.amount > 0 && (
                      <div>Payment 2: ${previewData.payments.payment_2.amount?.toLocaleString()} - {previewData.payments.payment_2.date}</div>
                    )}
                    {previewData.payments.payment_3.amount > 0 && (
                      <div>Payment 3 (Final): ${previewData.payments.payment_3.amount?.toLocaleString()} - {previewData.payments.payment_3.date}</div>
                    )}
                  </div>
                </div>
              )}

              {/* Payment Methods */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-[#1B4D3E] mb-3">Payment Methods</h3>
                <div className="text-sm space-y-1">
                  <div>E-Transfer: {previewData.payment_methods.etransfer_email} (password: {previewData.payment_methods.etransfer_password})</div>
                  <div>Cheque: Payable to {previewData.payment_methods.cheque_payable_to}</div>
                </div>
              </div>

              {/* Terms & Conditions */}
              <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                <h3 className="font-semibold text-[#1B4D3E] mb-3">Terms & Conditions (shown to renter)</h3>
                <div 
                  className="text-sm max-h-64 overflow-y-auto prose prose-sm"
                  dangerouslySetInnerHTML={{ __html: previewData.terms_and_conditions }}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={handleDownloadPreviewPdf}>
                  <Download className="w-4 h-4 mr-2" /> Download PDF
                </Button>
                <Button variant="outline" onClick={() => setShowPreviewModal(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSendContract} className="bg-[#1B4D3E] hover:bg-[#153E32]">
                  <Send className="w-4 h-4 mr-2" /> Send Contract
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog (Full Form) */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Renter</DialogTitle>
          </DialogHeader>
          <RenterForm
            initialData={renter}
            onSave={handleFormSave}
            onCancel={() => setShowEditDialog(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
